import { useEffect, useState, useCallback, useRef } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  addDoc,
  serverTimestamp,
  doc as fsDoc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { useToast } from './ui/Toast';
import Badge from './ui/Badge';
import {
  X,
  User as UserIcon,
  MapPin,
  Package,
  Star,
  StickyNote,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
} from 'lucide-react';
import { CITIES, cityName } from '../utils/cities';
import AdminNotes from './AdminNotes';

/**
 * Reusable user-details modal for both customers and drivers.
 *
 * Props:
 *   user            : the user object (must include id, type, name, etc.)
 *   onClose         : close callback
 *   actions         : optional ReactNode rendered inside the "Actions" panel.
 *                     Pass null/undefined to hide the Actions tab entirely.
 *   tabs            : optional list of tab keys to show. Defaults to a sensible
 *                     set per user type.
 *   onSelectOrder   : optional fn(orderId) — called when an order row is tapped.
 *   addressesPreloaded : optional array of address docs already loaded by parent.
 */
export default function UserDetailsModal({
  user,
  onClose,
  actions,
  onSelectOrder,
  addressesPreloaded,
}) {
  const isDriver = user?.type === 'driver';
  const isCustomer = user?.type === 'customer';

  const availableTabs = [
    { key: 'profile', label: 'Profile', icon: UserIcon },
    ...(isCustomer ? [{ key: 'addresses', label: 'Addresses', icon: MapPin }] : []),
    { key: 'orders', label: 'Orders', icon: Package },
    ...(isDriver ? [{ key: 'service-cities', label: 'Service Cities', icon: Building2 }] : []),
    ...(isDriver ? [{ key: 'ratings', label: 'Ratings', icon: Star }] : []),
    { key: 'notes', label: 'Admin Notes', icon: StickyNote },
    ...(actions ? [{ key: 'actions', label: 'Actions', icon: CheckCircle }] : []),
  ];

  const [activeTab, setActiveTab] = useState('profile');

  if (!user) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
              {(user.name || '?').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-slate-900 truncate">{user.name || 'Unnamed'}</h3>
              <p className="text-xs text-slate-500 truncate">{user.phoneNumber || user.email || user.id}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-3 border-b border-slate-200 overflow-x-auto">
          {availableTabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === t.key
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'profile' && <ProfileTab user={user} />}
          {activeTab === 'addresses' && (
            <AddressesTab userId={user.id} preloaded={addressesPreloaded} />
          )}
          {activeTab === 'orders' && (
            <OrdersTab userId={user.id} userType={user.type} onSelectOrder={onSelectOrder} />
          )}
          {activeTab === 'service-cities' && <ServiceCitiesTab user={user} />}
          {activeTab === 'ratings' && <RatingsTab driverId={user.id} />}
          {activeTab === 'notes' && (
            <AdminNotes
              parentSegments={['users', user.id]}
              targetType={user.type || 'user'}
              targetLabel={user.name || user.phoneNumber || user.id}
            />
          )}
          {activeTab === 'actions' && <div>{actions}</div>}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function ProfileTab({ user }) {
  const rows = [
    ['Name', user.name],
    ['Email', user.email],
    ['Phone', user.phoneNumber],
    ['Status', user.status],
    ['Wallet', user.wallet_balance != null ? `${Number(user.wallet_balance).toFixed(2)} JOD` : null],
    ['User ID', user.id],
  ].filter(([, v]) => v != null && v !== '');

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {rows.map(([k, v]) => (
        <div key={k} className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-500">{k}</p>
          <p className="text-sm font-medium text-slate-900 break-all mt-0.5">{String(v)}</p>
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------

function AddressesTab({ userId, preloaded }) {
  const [addresses, setAddresses] = useState(preloaded || null);
  const [loading, setLoading] = useState(!preloaded);

  useEffect(() => {
    if (preloaded) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users', userId, 'addresses'));
        setAddresses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
        setAddresses([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, preloaded]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading addresses…
      </div>
    );
  }
  if (!addresses || addresses.length === 0) {
    return <p className="text-slate-500 text-sm">No saved addresses.</p>;
  }
  return (
    <div className="space-y-3">
      {addresses.map((a) => (
        <div key={a.id} className="border border-slate-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-blue-600 mt-0.5" />
            <div className="flex-1 min-w-0">
              {a.title && <p className="font-semibold text-sm text-slate-900">{a.title}</p>}
              <p className="text-sm text-slate-700">
                {[a.area, a.city].filter(Boolean).join(', ') || a.address || '—'}
              </p>
              {a.address && a.address !== a.area && (
                <p className="text-xs text-slate-500 mt-0.5">{a.address}</p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------

function OrdersTab({ userId, userType, onSelectOrder }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const PAGE = 20;

  const fetchPage = useCallback(async (cursor) => {
    const field = userType === 'driver' ? 'driverId' : 'customerId';
    // Composite (field==, createdAt desc) needs an explicit Firestore
    // index. If it's missing the query throws — catch it below and fall
    // back to an unordered fetch so the tab still shows the driver's
    // orders instead of a permanent spinner. We sort client-side.
    try {
      const constraints = [where(field, '==', userId), orderBy('createdAt', 'desc'), limit(PAGE)];
      if (cursor) constraints.push(startAfter(cursor));
      const q = query(collection(db, 'orders'), ...constraints);
      const snap = await getDocs(q);
      return { snap, sortedClientSide: false };
    } catch (e) {
      const code = e?.code || '';
      const msg = String(e?.message || e || '');
      const isIndexMissing =
        code === 'failed-precondition' ||
        /index/i.test(msg);
      if (!isIndexMissing || cursor) throw e; // pagination fallback isn't safe without the index
      console.warn(
        `[OrdersTab] Firestore composite index missing for (${field} ==, createdAt desc). Falling back to unordered fetch. Fix by deploying the composite index.`,
      );
      const q = query(collection(db, 'orders'), where(field, '==', userId), limit(200));
      const snap = await getDocs(q);
      return { snap, sortedClientSide: true };
    }
  }, [userId, userType]);

  useEffect(() => {
    (async () => {
      setLoadError(null);
      try {
        const { snap, sortedClientSide } = await fetchPage(null);
        let docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (sortedClientSide) {
          docs.sort((a, b) => {
            const av = a.createdAt?.toMillis?.() ?? (a.createdAt ? new Date(a.createdAt).getTime() : 0);
            const bv = b.createdAt?.toMillis?.() ?? (b.createdAt ? new Date(b.createdAt).getTime() : 0);
            return bv - av;
          });
          setHasMore(false); // fallback already pulled up to 200 rows
          setLastDoc(null);
        } else {
          setLastDoc(snap.docs.at(-1) || null);
          setHasMore(snap.docs.length === PAGE);
        }
        setOrders(docs);
      } catch (e) {
        console.error('orders load error:', e);
        setLoadError(e?.message || 'Failed to load orders');
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchPage]);

  const loadMore = async () => {
    if (!lastDoc) return;
    setLoading(true);
    try {
      const { snap } = await fetchPage(lastDoc);
      setOrders((prev) => [...prev, ...snap.docs.map((d) => ({ id: d.id, ...d.data() }))]);
      setLastDoc(snap.docs.at(-1) || null);
      setHasMore(snap.docs.length === PAGE);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (ts) => {
    const d = ts?.toDate?.() || (ts ? new Date(ts) : null);
    return d ? d.toLocaleString('en-GB') : '—';
  };

  const counts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading orders…
      </div>
    );
  }
  if (loadError && orders.length === 0) {
    return (
      <div className="rounded-lg bg-red-50 ring-1 ring-red-200 px-3 py-2 text-sm text-red-700">
        Couldn't load orders: {loadError}
      </div>
    );
  }
  if (orders.length === 0) {
    return <p className="text-slate-500 text-sm">No orders yet.</p>;
  }

  return (
    <div className="space-y-3">
      {/* Status summary */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(counts).map(([s, n]) => (
          <span key={s} className="px-2 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-medium">
            {s}: <span className="font-bold">{n}</span>
          </span>
        ))}
      </div>

      {/* Orders */}
      <div className="space-y-2">
        {orders.map((o) => {
          const num = `#${String(o.orderNumber || '').padStart(6, '0')}`;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onSelectOrder && onSelectOrder(o.id)}
              className="w-full text-left border border-slate-200 rounded-lg p-3 hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-mono font-semibold text-sm">{num}</div>
                <StatusPill status={o.status} />
              </div>
              <div className="mt-1 text-xs text-slate-600 flex items-center gap-1">
                <Clock className="w-3 h-3" /> {fmtDate(o.createdAt)}
              </div>
              <div className="mt-1 text-xs text-slate-700 truncate">
                {(o.startPlace?.area || '?')}{' → '}{(o.endPlace?.area || '?')}
                {o.totalPrice != null && (
                  <span className="ml-2 text-slate-500">
                    · {Number(o.totalPrice).toFixed(2)} JOD
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {hasMore && (
        <InfiniteScrollSentinel loading={loading} onVisible={loadMore} />
      )}
    </div>
  );
}

/**
 * Renders an invisible sentinel below the last order row and auto-fires
 * `onVisible` when it scrolls into the modal's viewport. Keeps a small
 * "Loading…" indicator so the user has feedback while the next page
 * fetches. If IntersectionObserver isn't available (e.g. very old
 * browsers) we fall back to a manual button so the tab never gets stuck.
 */
function InfiniteScrollSentinel({ loading, onVisible }) {
  const ref = useRef(null);
  const firingRef = useRef(false);
  const onVisibleRef = useRef(onVisible);
  useEffect(() => { onVisibleRef.current = onVisible; }, [onVisible]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // Debounce: the observer can fire repeatedly while the sentinel
        // stays visible during pagination — guard against re-entrancy.
        if (entry.isIntersecting && !firingRef.current) {
          firingRef.current = true;
          try { onVisibleRef.current?.(); } finally {
            setTimeout(() => { firingRef.current = false; }, 300);
          }
        }
      },
      { rootMargin: '120px 0px' }, // start loading a bit before it enters
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="py-3 flex items-center justify-center">
      {loading ? (
        <span className="text-xs text-slate-500 inline-flex items-center gap-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading more…
        </span>
      ) : (
        // Manual fallback (also useful if the observer never fires in
        // a nested scroll container the polyfill can't see).
        <button
          type="button"
          onClick={() => onVisibleRef.current?.()}
          className="text-xs text-blue-600 hover:underline"
        >
          Load more
        </button>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    waiting: 'bg-amber-100 text-amber-800',
    accepted: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-blue-100 text-blue-800',
    returned_pending: 'bg-orange-100 text-orange-800',
    returned: 'bg-orange-100 text-orange-800',
    completed: 'bg-green-100 text-green-800',
    canceled: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${styles[status] || 'bg-slate-100 text-slate-700'}`}>
      {status}
    </span>
  );
}

// -----------------------------------------------------------------------------

function RatingsTab({ driverId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [driverDocRating, setDriverDocRating] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Stored aggregate on the driver doc (some places already cache it).
        const u = await getDoc(fsDoc(db, 'users', driverId));
        if (u.exists()) {
          const d = u.data();
          setDriverDocRating(typeof d.rating === 'number' ? d.rating : null);
        }
        const snap = await getDocs(collection(db, 'users', driverId, 'ratings'));
        const values = snap.docs
          .map((d) => Number(d.data().value))
          .filter((n) => Number.isFinite(n));
        if (values.length === 0) {
          setStats({ count: 0, avg: 0, dist: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } });
        } else {
          const sum = values.reduce((a, b) => a + b, 0);
          const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
          values.forEach((v) => {
            const rounded = Math.max(1, Math.min(5, Math.round(v)));
            dist[rounded] = (dist[rounded] || 0) + 1;
          });
          setStats({ count: values.length, avg: sum / values.length, dist });
        }
      } catch (e) {
        console.error('ratings load:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [driverId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading ratings…
      </div>
    );
  }
  if (!stats) return null;

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-amber-700 uppercase font-bold">Average</p>
          <div className="flex items-baseline gap-1.5 mt-1">
            <span className="text-3xl font-bold text-amber-900">
              {stats.count > 0 ? stats.avg.toFixed(2) : '—'}
            </span>
            <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
          </div>
          <p className="text-xs text-amber-700 mt-1">{stats.count} rating{stats.count === 1 ? '' : 's'}</p>
        </div>
        {driverDocRating != null && driverDocRating !== stats.avg && (
          <div className="text-right">
            <p className="text-xs text-slate-500">Stored on profile</p>
            <p className="text-sm font-mono">{Number(driverDocRating).toFixed(2)}</p>
          </div>
        )}
      </div>

      {/* Distribution */}
      <div className="space-y-1.5">
        {[5, 4, 3, 2, 1].map((star) => {
          const n = stats.dist[star] || 0;
          const pct = stats.count > 0 ? (n / stats.count) * 100 : 0;
          return (
            <div key={star} className="flex items-center gap-2">
              <span className="text-xs font-semibold w-6 text-slate-700">{star}★</span>
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-amber-400" style={{ width: `${pct}%` }} />
              </div>
              <span className="text-xs text-slate-600 w-8 text-right">{n}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

/**
 * Service Cities tab — driver only.
 *
 * Reads the driver's `serviceCities` from the user object the parent
 * already loaded (no extra Firestore round-trip). Shape:
 *   - missing/empty   → legacy driver, treated as "all cities"
 *   - ['all']         → explicitly opted into all cities
 *   - ['amman','irbid', ...] → specific city keys
 *
 * Display name is resolved via the same CITIES table the rest of the panel uses.
 */
function ServiceCitiesTab({ user }) {
  const raw = user?.serviceCities;
  const isLegacy = !Array.isArray(raw) || raw.length === 0;
  const isAll = !isLegacy && raw.includes('all');
  const cityKeys = isLegacy || isAll ? [] : raw;

  return (
    <div>
      {isLegacy && (
        <div className="rounded-lg bg-amber-50 ring-1 ring-amber-200 px-4 py-3 mb-4 text-sm text-amber-800">
          This driver hasn't picked their service cities yet. The broadcast
          treats them as <strong>all cities</strong> until they save.
        </div>
      )}
      {isAll && (
        <div className="rounded-lg bg-green-50 ring-1 ring-green-200 px-4 py-3 mb-4 text-sm text-green-800">
          Driver opted in to <strong>all cities</strong> — they receive every
          new-order broadcast regardless of pickup location.
        </div>
      )}
      {!isLegacy && !isAll && (
        <>
          <p className="text-sm text-slate-600 mb-3">
            Driver receives broadcasts only for orders whose pickup is in one
            of these {cityKeys.length} {cityKeys.length === 1 ? 'city' : 'cities'}:
          </p>
          <div className="flex flex-wrap gap-2">
            {cityKeys.map((k) => (
              <span
                key={k}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 ring-1 ring-blue-200 text-sm font-medium"
              >
                {cityName(k, 'en')}
                <span className="text-blue-400 text-xs">{cityName(k, 'ar')}</span>
              </span>
            ))}
          </div>
        </>
      )}

      {/* Reference: full canonical list, dimmed for cities not selected. */}
      <div className="mt-6 pt-4 border-t border-slate-200">
        <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">
          All cities
        </p>
        <div className="flex flex-wrap gap-1.5">
          {CITIES.map((c) => {
            const on = isLegacy || isAll || cityKeys.includes(c.key);
            return (
              <span
                key={c.key}
                className={`px-2 py-1 rounded text-xs ${
                  on
                    ? 'bg-slate-100 text-slate-700'
                    : 'bg-white text-slate-300 ring-1 ring-slate-200'
                }`}
              >
                {c.en}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
